"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.concentrationLimitPointerMethods = void 0;
const common_1 = require("@finley-technologies/common");
/**
 * This pointer method groups receivables by baseField name and then compares all the groups who
 * has a balance less than 3000 and compare that aggregate amount to the concentration limit.
 * If below concentration limit, then pro-rate kickout amount to each receivable for each group.
 */
const insuredNameGroupLimitPointer = (params) => {
    const { concentrationReceivables, valueConfig } = params;
    // *** Set up hardcoded values ***
    const baseFieldName = 'name';
    const dynamicNonEligibleInsured = 'nonEligibleInsured';
    // *** Set up recording variables ***
    let totalEligibleExposureLessConcentrationLimitKickouts = 0;
    const groupedReceivablesByNameMap = {};
    // *** Manage calculations by looping through each receivable ***
    for (const receivable of concentrationReceivables) {
        const { finleyReceivableId, baseFields, dynamicIntermediateFields, concentrationLimitFields, eligibilityFields, } = receivable;
        const { eligibleExposureLessConcentrationLimitKickouts, } = concentrationLimitFields;
        totalEligibleExposureLessConcentrationLimitKickouts += eligibleExposureLessConcentrationLimitKickouts;
        // We only care about eligible receivables
        if (!eligibilityFields.isEligible)
            continue;
        // Only want receivables that are not insured (so only want "FALSE")
        if (dynamicIntermediateFields[dynamicNonEligibleInsured] !== false) {
            continue;
        }
        const name = baseFields[baseFieldName];
        (0, common_1.finleyAssert)(typeof name === 'string', `nivodaI801.insuredGroupNameLimitPointer: Incurred unsupported name base field value [key:${baseFieldName}] [receivableId:${finleyReceivableId}]`);
        if (!(name in groupedReceivablesByNameMap)) {
            groupedReceivablesByNameMap[name] = {
                totalBalanceForGroup: 0,
                receivables: [],
            };
        }
        const existingRecord = groupedReceivablesByNameMap[name];
        existingRecord.totalBalanceForGroup = common_1.utils.jsRound({
            value: existingRecord.totalBalanceForGroup +
                eligibleExposureLessConcentrationLimitKickouts,
            precision: 4,
        });
        existingRecord.receivables.push(receivable);
    }
    // Only evaluating the grouped receivables by "name" below this balance
    const minimumGroupBalance = 3000;
    let totalBelowBalance = 0;
    const targetKickoutRecords = [];
    for (const [name, record] of Object.entries(groupedReceivablesByNameMap)) {
        const { totalBalanceForGroup, receivables } = record;
        // If group's total balance is equal to or above minimum, not part of concentration evaluation
        if (totalBalanceForGroup >= minimumGroupBalance)
            continue;
        totalBelowBalance = common_1.utils.jsRound({
            value: totalBelowBalance + totalBalanceForGroup,
            precision: 4,
        });
        targetKickoutRecords.push({ name, totalBalanceForGroup, receivables });
    }
    const percentToTotalBelowBalance = common_1.utils.divideByOrZero(totalBelowBalance, totalEligibleExposureLessConcentrationLimitKickouts);
    // The aggregate grouped balances below the minimum is below the concentration limit, so no kickouts needed
    if (percentToTotalBelowBalance < valueConfig.limit) {
        return {
            kickouts: {},
            calculations: {
                concentrationLimit: valueConfig.limit,
                concentrationAmount: totalEligibleExposureLessConcentrationLimitKickouts,
                concentration: percentToTotalBelowBalance,
            },
        };
    }
    // This is the amount we need to kickout
    const overagePercentDiff = common_1.utils.jsRound({
        value: percentToTotalBelowBalance - valueConfig.limit,
        precision: 8,
    });
    // *** Manage kickout calculations ***
    const kickouts = {};
    for (const { totalBalanceForGroup, receivables } of targetKickoutRecords) {
        const proRatePercent = common_1.utils.divideByOrZero(totalBalanceForGroup, totalBelowBalance);
        // Based on group's percent of total balance, this is the aggregate kickout
        // amount for the group, which is pro-rated to each receivable in the group
        const kickoutAmountForGroup = proRatePercent * overagePercentDiff;
        for (const receivable of receivables) {
            const { finleyReceivableId, concentrationLimitFields } = receivable;
            const { eligibleExposureLessConcentrationLimitKickouts, } = concentrationLimitFields;
            const specificReceivablePercent = common_1.utils.divideByOrZero(eligibleExposureLessConcentrationLimitKickouts, totalBalanceForGroup);
            const kickoutAmountForReceivable = specificReceivablePercent * kickoutAmountForGroup;
            // Recording the kickout receivable and amount
            kickouts[finleyReceivableId] = kickoutAmountForReceivable;
        }
    }
    return {
        kickouts,
        calculations: {
            concentrationLimit: valueConfig.limit,
            concentrationAmount: totalEligibleExposureLessConcentrationLimitKickouts,
            concentration: percentToTotalBelowBalance,
        },
    };
};
exports.concentrationLimitPointerMethods = [
    {
        method: 'nivodaI801.insuredNameGroupLimit',
        pointerImplementation: insuredNameGroupLimitPointer,
    },
];
//# sourceMappingURL=concentrationLimitPointerMethods.js.map